import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LeftOutlined, RightOutlined } from '@ant-design/icons'
import { Button, Card, Popover, Space, Spin, message } from 'antd'
import dayjs from 'dayjs'
import PageHeader from '@/components/PageHeader'
import Permission from '@/components/PermissionA5'
import { DatePickerC, SelectC } from '@/i2antdV5'
import { formatDayString, parseHmStringToDayMinutes } from '@/utils/renderUtils'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { fetchTuinaReservationGrouped } from '@/redux/tuina/reservationGroupedSlice'
import { fetchTuinaShiftList } from '@/redux/tuina/shiftListSlice'
import { TUINA_RESERVATION_STATUS_OPTIONS } from '@/views/tuina/options'
import DispatchModal from './share/DispatchModal'
import ReservationDetailModal from './share/ReservationDetailModal'
import ReservationCreateModal from './share/ReservationCreateModal'
import './Page.less'

const breadcrumbList = [
  { breadcrumbName: '按摩' },
  { breadcrumbName: '日看板' }
]

const SLOT_MINUTES = 10
const SLOT_PX = 28
const HOUR_PX = (60 / SLOT_MINUTES) * SLOT_PX
const NAME_COL_PX = 108
const FALLBACK_AXIS_START = 10 * 60
const FALLBACK_AXIS_END = 20 * 60

function formatMinutes (total) {
  const hh = Math.floor(total / 60)
  const mm = total % 60
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`
}

function formatReservationClock (raw) {
  if (!raw) return ''
  const minutes = parseHmStringToDayMinutes(raw)
  if (minutes == null) return ''
  return formatMinutes(minutes)
}

function reservationStartMinutes (item) {
  return parseHmStringToDayMinutes(item.startTime)
}

function reservationEndClock (item) {
  const end = formatReservationClock(item.endTime)
  if (end) return end
  const start = reservationStartMinutes(item)
  const duration = Number(item.durationMinutes) || 0
  if (start == null) return ''
  return formatMinutes(start + duration)
}

function parseShiftClockMinutes (raw, isEnd) {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (isEnd && Number.isFinite(n) && n === 1440) return 1440
  return parseHmStringToDayMinutes(raw)
}

function timeToOffsetFromMinutes (minutes, axisStart) {
  return (minutes - axisStart) / 60 * HOUR_PX
}

function cardLayout (item, axisStart) {
  const start = reservationStartMinutes(item)
  const left = start == null ? 0 : timeToOffsetFromMinutes(start, axisStart)
  const duration = Number(item.durationMinutes)
  const endMinutes = parseHmStringToDayMinutes(item.endTime)
  let widthMinutes = 0
  if (Number.isFinite(duration) && duration > 0) {
    widthMinutes = duration
  } else if (endMinutes != null && start != null && endMinutes > start) {
    widthMinutes = endMinutes - start
  }
  return {
    left,
    width: Math.max(widthMinutes / 60 * HOUR_PX, 48)
  }
}

function nowLineLeft (day, now, axisStart, axisEnd) {
  if (!day || !now || !day.isSame(now, 'day')) return null
  const minutes = now.hour() * 60 + now.minute()
  if (minutes < axisStart || minutes >= axisEnd) return null
  return (minutes - axisStart) / 60 * HOUR_PX
}

function hhmmToMinutes (hhmm) {
  const parts = String(hhmm).split(':')
  return Number(parts[0]) * 60 + Number(parts[1] || 0)
}

function itemSpan (item) {
  const start = reservationStartMinutes(item)
  const duration = Number(item.durationMinutes) || 0
  const endFromField = parseHmStringToDayMinutes(item.endTime)
  const startMinutes = start == null ? 0 : start
  const end = endFromField != null && endFromField > startMinutes
    ? endFromField
    : startMinutes + duration
  return { start: startMinutes, end }
}

function clusterUnassigned (items) {
  const sorted = [...items].sort((a, b) => {
    const sa = reservationStartMinutes(a)
    const sb = reservationStartMinutes(b)
    const na = sa == null ? 0 : sa
    const nb = sb == null ? 0 : sb
    if (na !== nb) return na - nb
    return String(a.id).localeCompare(String(b.id))
  })
  const clusters = []
  sorted.forEach((item) => {
    const span = itemSpan(item)
    const last = clusters[clusters.length - 1]
    if (!last || span.start >= last.end) {
      clusters.push({ start: span.start, end: span.end, items: [item] })
      return
    }
    last.items.push(item)
    if (span.end > last.end) last.end = span.end
  })
  return clusters
}

function slotLabel (total) {
  const mm = total % 60
  if (mm === 0) return formatMinutes(total)
  if (mm === 30) return '30'
  return ''
}

function clusterLayout (cluster, axisStart) {
  const left = (cluster.start - axisStart) / 60 * HOUR_PX
  const width = (cluster.end - cluster.start) / 60 * HOUR_PX
  return {
    left,
    width: Math.max(width, 48)
  }
}

function reservationStatusText (status) {
  const row = TUINA_RESERVATION_STATUS_OPTIONS.find((item) => item.id === status)
  return (row && row.value) || status || ''
}

function AppointmentCardFace ({ item }) {
  const name = item.memberName || item.customer
  const project = item.projectName || item.reservationNo
  return (
    <>
      <div className="tuina-board-card-title">{name || project}</div>
      {name && project ? (
        <div className="tuina-board-card-sub">{project}</div>
      ) : item.reservationNo && item.projectName ? (
        <div className="tuina-board-card-sub">{item.reservationNo}</div>
      ) : null}
      <div className="tuina-board-card-time">
        {formatReservationClock(item.startTime)}
        {' 至 '}
        {reservationEndClock(item)}
      </div>
    </>
  )
}

function UnassignedClusterChip ({ cluster, selectedId, open, onOpenChange, onClickItem, axisStart }) {
  const n = cluster.items.length
  const layout = clusterLayout(cluster, axisStart)
  const selected = cluster.items.some((item) => String(item.id) === String(selectedId))
  const one = n === 1 ? cluster.items[0] : null
  const chip = (
    <button
      type="button"
      className={[
        'tuina-board-card',
        'is-unassigned',
        selected ? 'is-selected' : ''
      ].filter(Boolean).join(' ')}
      aria-pressed={selected}
      aria-label={
        one
          ? `${one.memberName || one.customer || one.projectName || one.reservationNo} ${formatReservationClock(one.startTime)} 至 ${reservationEndClock(one)} 待派`
          : `${formatMinutes(cluster.start)} 至 ${formatMinutes(cluster.end)} 待派 ${n} 单，点开列表`
      }
      onClick={() => {
        if (!one) return
        onClickItem(one)
      }}
    >
      {one ? (
        <AppointmentCardFace item={one} />
      ) : (
        <>
          <div className="tuina-board-card-title">
            待派
            {' '}
            {n}
            {' '}
            单
          </div>
          <div className="tuina-board-card-sub">
            {formatMinutes(cluster.start)}
            {' 至 '}
            {formatMinutes(cluster.end)}
          </div>
          <div className="tuina-board-card-time">点开列表再派</div>
        </>
      )}
    </button>
  )
  return (
    <div
      className="tuina-board-queue-anchor"
      style={{
        left: layout.left + 3,
        width: layout.width - 6
      }}
    >
      {one ? chip : (
        <Popover
          trigger="click"
          open={open}
          onOpenChange={onOpenChange}
          destroyTooltipOnHide
          placement="bottomLeft"
          getPopupContainer={() => document.body}
          title={`待派 ${n} 单`}
          content={(
            <div className="tuina-board-queue" role="list">
              {cluster.items.map((item) => (
                <button
                  type="button"
                  role="listitem"
                  key={item.id}
                  className="tuina-board-queue-item"
                  onClick={() => onClickItem(item)}
                >
                  <div className="tuina-board-queue-item-title">
                    {item.memberName || item.customer || item.projectName || item.reservationNo}
                  </div>
                  <div className="tuina-board-queue-item-sub">
                    {item.projectName || item.reservationNo}
                    {' · '}
                    {formatReservationClock(item.startTime)}
                    {' 至 '}
                    {reservationEndClock(item)}
                  </div>
                </button>
              ))}
            </div>
          )}
        >
          {chip}
        </Popover>
      )}
    </div>
  )
}

function timeOverlaps (items, therapistId, startHhmm, durationMinutes, ignoreId, dateKey) {
  const start = hhmmToMinutes(startHhmm)
  const end = start + (Number(durationMinutes) || 0)
  return items.some((item) => {
    if (ignoreId && String(item.id) === String(ignoreId)) return false
    if (!item.therapistId) return false
    if (String(item.therapistId) !== String(therapistId)) return false
    if (item.status === 'completed' || item.status === 'cancelled' || item.status === 'no_show') return false
    if (dateKey && item.appointmentDate && formatDayString(item.appointmentDate) !== dateKey) return false
    const span = itemSpan(item)
    return start < span.end && end > span.start
  })
}

function isPastSlot (day, startMinutes, now) {
  if (!day || !now) return false
  if (day.isBefore(now, 'day')) return true
  if (!day.isSame(now, 'day')) return false
  return startMinutes < now.hour() * 60 + now.minute()
}

function toBoardApiQuery (formatQuery) {
  const apiQuery = {}
  if (formatQuery.date) apiQuery.date = formatQuery.date
  if (formatQuery.storeId != null && formatQuery.storeId !== '') apiQuery.storeId = formatQuery.storeId
  return apiQuery
}

/**
 * 按摩日看板：一行一个师傅，横轴时间。列表走 GET /tuina/reservation/grouped。
 */
export default function TuinaBoardPage () {
  const dispatch = useDispatch()
  const buttonCodes = useSelector((state) => state.app.permissionCodes.buttonCodes)
  const reservationGrouped = useSelector((state) => state.tuina.reservationGrouped)
  const shiftList = useSelector((state) => state.tuina.shiftList)
  const getJStoreList = useSelector((state) => state.components.getJStoreList)
  const canBoardControl = Array.isArray(buttonCodes) && buttonCodes.includes('tuina:board:control')
  const [query, setQuery] = useState(() => ({
    date: dayjs().startOf('day').valueOf()
  }))
  const [now, setNow] = useState(() => dayjs())
  const [selectedId, setSelectedId] = useState(null)
  const [queueOpenKey, setQueueOpenKey] = useState(null)
  const [localList, setLocalList] = useState(null)
  const didInitStore = useRef(false)
  const prevStoreFetching = useRef(false)

  const groups = Array.isArray(reservationGrouped.data) ? reservationGrouped.data : []
  const apiList = useMemo(() => {
    const out = []
    groups.forEach((g) => {
      const rows = Array.isArray(g.reservations) ? g.reservations : []
      rows.forEach((r) => out.push(r))
    })
    return out
  }, [groups])

  useEffect(() => {
    setLocalList(null)
  }, [reservationGrouped.data])

  const list = localList || apiList
  const therapistRows = groups.filter((g) => g.therapistId)
  const boardRows = [
    { therapistId: null, therapistName: '待派' },
    ...therapistRows
  ]
  const therapistOptions = therapistRows.map((g) => ({
    id: g.therapistId,
    value: g.therapistName
  }))

  const day = useMemo(() => dayjs(query.date), [query.date])

  const axis = useMemo(() => {
    let axisStart = FALLBACK_AXIS_START
    let axisEnd = FALLBACK_AXIS_END
    const shiftStoreId = shiftList.query.storeId
    const boardStoreId = query.storeId
    const storeMatch = (boardStoreId == null || boardStoreId === '')
      ? (shiftStoreId == null || shiftStoreId === '')
      : String(shiftStoreId) === String(boardStoreId)
    const rows = storeMatch && Array.isArray(shiftList.data.list) ? shiftList.data.list : []
    const defaultShift = rows.find((row) => row.isDefault === true)
    if (defaultShift) {
      const start = parseShiftClockMinutes(defaultShift.startTime, false)
      const end = parseShiftClockMinutes(defaultShift.endTime, true)
      if (start != null && end != null && end > start) {
        axisStart = start
        axisEnd = end
      }
    }
    const slots = []
    for (let m = axisStart; m < axisEnd; m += SLOT_MINUTES) slots.push(m)
    return {
      axisStart,
      axisEnd,
      slots,
      trackWidth: (axisEnd - axisStart) / 60 * HOUR_PX,
      timeOptions: slots.map((m) => {
        const label = formatMinutes(m)
        return { id: label, value: label }
      })
    }
  }, [shiftList.data, shiftList.query.storeId, query.storeId])

  const fetchBoard = (formatQuery) => {
    if (!formatQuery.date) return
    setQuery(formatQuery)
    dispatch(fetchTuinaReservationGrouped({ query: toBoardApiQuery(formatQuery) }))
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载日看板失败'))
  }

  const setDay = (next) => {
    fetchBoard({
      ...query,
      date: next.startOf('day').valueOf()
    })
  }

  useEffect(() => {
    dispatch(fetchJStoreList({ query: {}, dataType: 'optionTuina' }))
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载门店列表失败'))
  }, [dispatch])

  useEffect(() => {
    const wasFetching = prevStoreFetching.current
    prevStoreFetching.current = getJStoreList.fetching
    if (didInitStore.current) return
    if (getJStoreList.fetching) return
    if (!wasFetching) return
    didInitStore.current = true
    const options = getJStoreList.data
    const first = Array.isArray(options) && options[0]
    const next = {
      date: dayjs().startOf('day').valueOf()
    }
    if (first && first.id != null && first.id !== '') next.storeId = first.id
    fetchBoard(next)
  }, [getJStoreList.fetching, getJStoreList.data])

  useEffect(() => {
    const shiftQuery = { pageNumber: 1, pageSize: 200 }
    if (query.storeId != null && query.storeId !== '') shiftQuery.storeId = query.storeId
    dispatch(fetchTuinaShiftList({ query: shiftQuery }))
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载班次失败'))
  }, [dispatch, query.storeId])

  useEffect(() => {
    const tick = () => setNow(dayjs())
    const timer = setInterval(tick, 30 * 1000)
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const dispatchModalRef = useRef(null)
  const reservationDetailModalRef = useRef(null)
  const scrollRef = useRef(null)

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const left = nowLineLeft(day, dayjs(), axis.axisStart, axis.axisEnd)
    if (left == null) {
      el.scrollLeft = 0
      return
    }
    const visibleTrack = el.clientWidth - NAME_COL_PX
    const next = left - visibleTrack / 2
    const max = el.scrollWidth - el.clientWidth
    el.scrollLeft = Math.min(max, Math.max(0, next))
  }, [day, axis.axisStart, axis.axisEnd, axis.trackWidth])

  const nowLeft = nowLineLeft(day, now, axis.axisStart, axis.axisEnd)
  const boardDayKey = formatDayString(day)
  const dayList = useMemo(
    () => list.filter((item) => {
      if (item.status === 'cancelled') return false
      if (!item.appointmentDate) return true
      return formatDayString(item.appointmentDate) === boardDayKey
    }),
    [list, boardDayKey]
  )

  const openReservationDetail = (item) => {
    if (!item || item.id == null || item.id === '') return
    setSelectedId(item.id)
    setQueueOpenKey(null)
    reservationDetailModalRef.current?.show(item)
  }

  const openDispatch = (item) => {
    if (!item || item.therapistId || item.status !== 'booked') return
    setQueueOpenKey(null)
    dispatchModalRef.current?.show(item)
  }

  const handleDispatchSubmit = async (payload) => {
    const { id, durationMinutes, technicianId, start, date } = payload
    if (!id) throw new Error('missing appointment')
    const tech = therapistRows.find((row) => String(row.therapistId) === String(technicianId))
    if (!tech) {
      message.warning('请选择师傅')
      throw new Error('tech')
    }
    if (!date) {
      message.warning('请选择日期')
      throw new Error('date')
    }
    const current = dayjs()
    setNow(current)
    const targetDay = dayjs(date)
    if (isPastSlot(targetDay, hhmmToMinutes(start), current)) {
      message.warning('过去的时间不能预约')
      throw new Error('past')
    }
    if (timeOverlaps(list, technicianId, start, durationMinutes, id, date)) {
      message.warning('这个时间已经有人')
      throw new Error('conflict')
    }
    const startMinutes = hhmmToMinutes(start)
    setLocalList(list.map((row) => {
      if (String(row.id) !== String(id)) return row
      return {
        ...row,
        therapistId: technicianId,
        therapistName: tech.therapistName,
        startTime: startMinutes,
        endTime: startMinutes + (Number(durationMinutes) || 0),
        appointmentDate: targetDay.startOf('day').valueOf()
      }
    }))
    if (!targetDay.isSame(day, 'day')) setDay(targetDay)
    setSelectedId(id)
    message.success(`已派给${tech.therapistName}`)
  }

  const idleTechnicianOptions = (date, start, ignoreId, durationMinutes) => {
    if (!date || !start) return []
    if (isPastSlot(dayjs(date), hhmmToMinutes(start), dayjs())) return []
    return therapistOptions.filter((opt) => {
      return !timeOverlaps(list, opt.id, start, durationMinutes, ignoreId, date)
    })
  }

  const handleCreateReservationSuccess = () => {
    fetchBoard(query)
  }

  const jStoreOptions = getJStoreList.data

  return (
    <div>
      <PageHeader title="日看板" breadcrumbList={breadcrumbList} />
      <div className="i2-container">
        <Card className="i2-card-page">
          <section className="i2-app-toolbar tuina-board-toolbar">
            <Space wrap size={8}>
              <SelectC
                label="门店"
                query={query}
                searchKey="storeId"
                options={jStoreOptions}
                action={fetchBoard}
                placeholder="全部"
                modalProps={{ allowClear: true, size: 'large' }}
              />
              <Button
                size="large"
                icon={<LeftOutlined />}
                onClick={() => setDay(day.subtract(1, 'day'))}
                aria-label="前一天"
              />
              <Button size="large" onClick={() => setDay(dayjs())}>今天</Button>
              <Button
                size="large"
                icon={<RightOutlined />}
                onClick={() => setDay(day.add(1, 'day'))}
                aria-label="后一天"
              />
              <DatePickerC
                label="日期"
                query={query}
                searchKey="date"
                action={fetchBoard}
                modalProps={{ allowClear: false, size: 'large' }}
              />
              <span className="tuina-board-toolbar-note">待派同一时段只显示单数，点开列表再派。先选日期和时间，再选空闲师傅。</span>
            </Space>
            <div className="tuina-board-toolbar-right">
              <div className="tuina-board-legend">
                <span className="tuina-board-legend-item is-unassigned">待派</span>
                <span className="tuina-board-legend-item is-booked">还没开始</span>
                <span className="tuina-board-legend-item is-no_show">没来</span>
              </div>
              <Permission rootPath="tuinaPage">
                <span pathitem={['board']} checkarray={['buttonCodes']}>
                  <ReservationCreateModal
                    storeId={query.storeId}
                    date={query.date}
                    onSuccess={handleCreateReservationSuccess}
                  >
                    <Button type="primary" size="large">新建订单</Button>
                  </ReservationCreateModal>
                </span>
              </Permission>
            </div>
          </section>

          <Spin spinning={reservationGrouped.fetching || shiftList.fetching}>
            <div className="tuina-board-scroll" ref={scrollRef}>
              <div className="tuina-board-inner" style={{ width: NAME_COL_PX + axis.trackWidth }}>
                <div className="tuina-board-head">
                  <div className="tuina-board-name">师傅</div>
                  <div className="tuina-board-hours" style={{ width: axis.trackWidth }}>
                    {axis.slots.map((m) => {
                      const mm = m % 60
                      const hourLine = (m + SLOT_MINUTES) % 60 === 0
                      const past = isPastSlot(day, m, now)
                      return (
                        <div
                          key={m}
                          className={[
                            'tuina-board-hour',
                            mm === 0 ? 'is-hour' : '',
                            mm === 30 ? 'is-half' : '',
                            hourLine ? 'is-hour-line' : '',
                            past ? 'is-past' : ''
                          ].filter(Boolean).join(' ')}
                          style={{ width: SLOT_PX }}
                        >
                          {slotLabel(m)}
                        </div>
                      )
                    })}
                    {nowLeft != null ? (
                      <div className="tuina-board-now is-head" style={{ left: nowLeft }}>
                        <span className="tuina-board-now-label">现在</span>
                      </div>
                    ) : null}
                  </div>
                </div>
                {boardRows.map((row) => {
                  const unassignedRow = !row.therapistId
                  const rowItems = dayList.filter((item) => (
                    unassignedRow ? !item.therapistId : String(item.therapistId) === String(row.therapistId)
                  ))
                  const rowKey = unassignedRow ? 'unassigned' : String(row.therapistId)
                  const rowName = row.therapistName || ''
                  return (
                    <div
                      key={rowKey}
                      className={['tuina-board-row', unassignedRow ? 'is-unassigned' : ''].filter(Boolean).join(' ')}
                    >
                      <div className="tuina-board-name">
                        {row.avatarUrl ? (
                          <img className="tuina-board-tech-mark" src={row.avatarUrl} alt="" />
                        ) : (
                          <span className="tuina-board-tech-mark" aria-hidden="true">
                            {rowName.slice(0, 1)}
                          </span>
                        )}
                        <span>{rowName}</span>
                      </div>
                      <div className="tuina-board-track" style={{ width: axis.trackWidth }}>
                        {axis.slots.map((m) => {
                          const hourLine = (m + SLOT_MINUTES) % 60 === 0
                          const past = isPastSlot(day, m, now)
                          return (
                            <div
                              key={m}
                              className={[
                                'tuina-board-slot',
                                hourLine ? 'is-hour-line' : '',
                                m % 60 === 30 ? 'is-half' : '',
                                past ? 'is-past' : ''
                              ].filter(Boolean).join(' ')}
                              style={{ width: SLOT_PX }}
                            />
                          )
                        })}
                        {nowLeft != null ? (
                          <div className="tuina-board-now" style={{ left: nowLeft }} />
                        ) : null}
                        {unassignedRow
                          ? clusterUnassigned(rowItems).map((cluster) => {
                            const clusterKey = `q-${cluster.items[0].id}`
                            return (
                              <UnassignedClusterChip
                                key={clusterKey}
                                cluster={cluster}
                                selectedId={selectedId}
                                open={queueOpenKey === clusterKey}
                                onOpenChange={(nextOpen) => setQueueOpenKey(nextOpen ? clusterKey : null)}
                                onClickItem={openReservationDetail}
                                axisStart={axis.axisStart}
                              />
                            )
                          })
                          : rowItems.map((item) => {
                            const layout = cardLayout(item, axis.axisStart)
                            return (
                              <button
                                type="button"
                                key={item.id}
                                className={[
                                  'tuina-board-card',
                                  `is-${item.status}`,
                                  String(selectedId) === String(item.id) ? 'is-selected' : ''
                                ].filter(Boolean).join(' ')}
                                style={{
                                  left: layout.left + 3,
                                  width: layout.width - 6
                                }}
                                aria-pressed={String(selectedId) === String(item.id)}
                                aria-label={`${item.memberName || item.customer || item.projectName || item.reservationNo} ${formatReservationClock(item.startTime)} 至 ${reservationEndClock(item)} ${reservationStatusText(item.status)}`}
                                onClick={() => openReservationDetail(item)}
                              >
                                <AppointmentCardFace item={item} />
                              </button>
                            )
                          })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Spin>
          <ReservationDetailModal
            ref={reservationDetailModalRef}
            onDispatch={openDispatch}
            onSuccess={handleCreateReservationSuccess}
          />
          <DispatchModal
            ref={dispatchModalRef}
            date={boardDayKey}
            startOptions={axis.timeOptions}
            idleTechnicianOptions={idleTechnicianOptions}
            onSuccess={handleDispatchSubmit}
          />
        </Card>
      </div>
    </div>
  )
}
